import type { Sprite } from 'pixi.js';
import type { GlowstickAttack, RappelEffect } from '../../data/full/superfan';
import { clamp01, lerp, smoothstep } from '../../shared/scalar';
import { GLOWSTICK, frontRowPower } from '../../sim/full/attacks/glowstick';
import { RAPPEL } from '../../sim/full/skills/rappel';
import type { PlayerState, SimEvent, SimState, ZoneState } from '../../sim/state';
import { statValue } from '../../sim/stats';
import { createBurster } from '../class-bursts';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { type Shape, type Sheet, paintSheet, paintedLater } from '../paint';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, byId, hide, setTint } from '../util';
import { ViewPool } from '../views';
import { stickColor, stickSide } from './superfan';
import { GLOWSTICK_PIECES } from './superfan-paint';

type StickShapes = Readonly<Record<keyof typeof GLOWSTICK_PIECES, Shape>>;

const SIZE = 1.5;
const FLIGHT = { rise: 0.2, turns: 12, calmTurns: 2 };
const PLANTED = { glow: 20, glowAlpha: 0.3, beatAlpha: 0.35, frontGlow: 0.5 };
const PULSE_RING = { from: 6, speed: 1.6, alpha: 0.6, calmAlpha: 0.22, front: 0.5 };
const FADE_TICKS = 6;
const CALL = { from: 4, to: 22, beats: 0.4, alpha: 0.7 } as const;
const CHARGE = { from: 0.55, to: 6, alpha: 0.55, glow: 0.3, glowAlpha: 0.55, calm: 0.5 } as const;
const BURST = { beats: 0.55, from: 8, inner: 0.7, innerAlpha: 0.7, glow: 0.4 } as const;
const RELIGHT = { from: 0.4, to: 1 } as const;
const TWIST = 2.4;

interface StickView {
  readonly shadow: Sprite;
  readonly glow: Sprite;
  readonly ring: Sprite;
  readonly call: Sprite;
  readonly body: Sprite;
}

interface Landing {
  x: number;
  y: number;
  side: -1 | 1;
  seen: number;
}

export function createGlowsticks(ctx: RenderContext): Family {
  const { layers, textures } = ctx;
  const sheet = paintedLater((): Sheet<keyof typeof GLOWSTICK_PIECES> =>
    paintSheet(GLOWSTICK_PIECES),
  );
  const burster = createBurster(ctx, 48);
  const attacks = new Map<string, GlowstickAttack>();
  for (const [classId, attack] of ctx.attacks) {
    if (attack.kind === 'glowstick') {
      attacks.set(classId, attack);
    }
  }
  const rappels = new Map<string, RappelEffect>();
  for (const [classId, effect] of ctx.skillEffects) {
    if (effect.kind === 'rappel') {
      rappels.set(classId, effect);
    }
  }
  const zoneIds = new Set([...attacks.values()].map((attack) => attack.zone.id));
  const landings = new Map<number, Landing>();
  const sides = new Map<number, -1 | 1>();
  // A stick's time left only grows when a Rappel relights it, the moment it bursts.
  const lefts = new Map<number, number>();
  const relit = new Map<number, number>();
  const calling = new Set<number>();
  let flying: ViewPool<StickView> | undefined;
  let planted: ViewPool<StickView> | undefined;
  let image = 0;

  function pool(shapes: StickShapes): ViewPool<StickView> {
    return new ViewPool<StickView>(
      () => ({
        shadow: add(layers.weapons, shapes.stickShadow),
        ring: add(layers.zones, textures.ring),
        call: add(layers.zones, textures.ring),
        glow: add(layers.glow, textures.halo),
        body: add(layers.weapons, shapes.stick),
      }),
      (view) => {
        hide(view.shadow, view.ring, view.call, view.glow, view.body);
      },
    );
  }

  function attackOf(player: PlayerState): GlowstickAttack | undefined {
    return attacks.get(player.classId);
  }

  function drawFlying(state: SimState, alpha: number, frame: Frame, shapes: StickShapes): void {
    flying ??= pool(shapes);
    flying.begin();
    const { calm, palette, light } = frame;
    for (const prop of state.props ?? []) {
      const owner = prop.kind === GLOWSTICK ? byId(state.players, prop.playerId) : undefined;
      const attack = owner === undefined ? undefined : attackOf(owner);
      if (owner === undefined || attack === undefined) {
        continue;
      }
      const side = stickSide(prop.rank ?? 0);
      landings.set(prop.id, {
        x: prop.x + prop.vx * prop.ticksLeft,
        y: prop.y + prop.vy * prop.ticksLeft,
        side,
        seen: image,
      });
      const view = flying.acquire(prop.id);
      const flown = Math.min(1, 1 - (prop.ticksLeft - alpha) / attack.flightTicks);
      const length = Math.hypot(prop.vx, prop.vy) * attack.flightTicks;
      const lift = FLIGHT.rise * length * Math.sin(Math.PI * flown);
      const x = lerp(prop.prevX, prop.x, alpha);
      const y = lerp(prop.prevY, prop.y, alpha);
      const size = (SIZE * owner.radius) / REFERENCE_RADIUS;
      const turn = Math.atan2(prop.vy, prop.vx) + flown * (calm ? FLIGHT.calmTurns : FLIGHT.turns);
      const color = stickColor(palette, side);

      view.shadow.visible = true;
      view.shadow.position.set(x + lift * 0.35, y + lift * 0.2);
      view.shadow.rotation = turn;
      view.shadow.scale.set(size * (1 - lift / 80));
      view.shadow.alpha = 0.22 * (1 - lift / 60);
      setTint(view.shadow, 0x000000);

      view.body.visible = true;
      view.body.position.set(x, y - lift);
      view.body.rotation = turn;
      view.body.scale.set(size);
      setTint(view.body, color);

      view.glow.visible = true;
      view.glow.position.set(x, y - lift);
      view.glow.scale.set((PLANTED.glow * owner.radius) / 14 / textures.halo.radius);
      view.glow.alpha = light.haloAlpha * 0.7;
      setTint(view.glow, color);
      view.ring.visible = false;
      view.call.visible = false;
    }
    flying.end();
  }

  function sideOf(zone: ZoneState): -1 | 1 {
    let side = sides.get(zone.id);
    if (side !== undefined) {
      return side;
    }
    let nearest = Infinity;
    let found: number | undefined;
    for (const [id, landing] of landings) {
      const away = Math.hypot(landing.x - zone.x, landing.y - zone.y);
      if (landing.seen !== image && away < nearest) {
        nearest = away;
        found = id;
      }
    }
    side = found === undefined ? stickSide(zone.id) : (landings.get(found)?.side ?? 1);
    if (found !== undefined) {
      landings.delete(found);
    }
    sides.set(zone.id, side);
    return side;
  }

  function burstRadius(owner: PlayerState): number | undefined {
    const effect = rappels.get(owner.classId);
    return effect === undefined ? undefined : effect.radius * statValue(owner, 'skillRadiusMul', 1);
  }

  function white(frame: Frame): number {
    return frame.light.additive ? 0xffffff : frame.palette.texte;
  }

  function burst(zone: ZoneState, owner: PlayerState, frame: Frame): void {
    const reach = burstRadius(owner);
    if (reach === undefined) {
      return;
    }
    const color = stickColor(frame.palette, sideOf(zone));
    const duration = BURST.beats * frame.tempo.ticksPerBeat;
    const at = { now: frame.now, duration, x: zone.x, y: zone.y };
    burster.spawn(frame, {
      ...at,
      shape: textures.halo,
      fromRadius: reach,
      toRadius: reach,
      tint: color,
      peak: BURST.glow * frame.light.haloAlpha,
    });
    burster.spawn(frame, {
      ...at,
      shape: textures.ring,
      fromRadius: BURST.from,
      toRadius: reach,
      tint: color,
      peak: 1,
      outline: true,
    });
    burster.spawn(frame, {
      ...at,
      shape: textures.ring,
      fromRadius: BURST.from / 2,
      toRadius: reach * BURST.inner,
      tint: white(frame),
      peak: BURST.innerAlpha,
    });
  }

  function drawPlanted(
    view: StickView,
    zone: ZoneState,
    owner: PlayerState,
    attack: GlowstickAttack,
    state: SimState,
    frame: Frame,
    alpha: number,
  ): void {
    const { calm, palette, light, tempo, now, pulse } = frame;
    const beat = tempo.ticksPerBeat;
    const total = zone.definition.durationBars * tempo.ticksPerBar;
    const since = total - zone.ticksLeft + alpha;
    const burstAt = relit.get(zone.id);
    const relighting =
      burstAt === undefined
        ? 1
        : clamp01(((now - burstAt) / beat - RELIGHT.from) / (RELIGHT.to - RELIGHT.from));
    const shown = clamp01(Math.min(since / 2, (zone.ticksLeft - alpha) / FADE_TICKS)) * relighting;
    const front = Math.min(
      1,
      (frontRowPower(attack, state.core, zone) - 1) / Math.max(1e-6, attack.frontRow.bonus),
    );
    const reach = burstRadius(owner);
    const charge = reach !== undefined && calling.has(owner.id) ? (now % beat) / beat : 0;
    const charged = charge * (calm ? CHARGE.calm : 1);
    const color = stickColor(palette, sideOf(zone));
    const size = (SIZE * owner.radius) / REFERENCE_RADIUS;
    const glowScale = owner.radius / 14;

    view.shadow.visible = false;
    view.body.visible = true;
    view.body.position.set(zone.x, zone.y);
    view.body.rotation = Math.atan2(zone.direction.y, zone.direction.x) + zone.id * TWIST;
    view.body.scale.set(size);
    view.body.alpha = shown;
    setTint(view.body, color);

    view.glow.visible = true;
    view.glow.position.set(zone.x, zone.y);
    view.glow.scale.set(
      (PLANTED.glow * glowScale * (1 + PLANTED.frontGlow * front) * (1 + CHARGE.glow * charged)) /
        textures.halo.radius,
    );
    view.glow.alpha = Math.min(
      1,
      shown *
        light.haloAlpha *
        ((PLANTED.glowAlpha + PLANTED.beatAlpha * pulse) * (1 + PLANTED.frontGlow * front) +
          CHARGE.glowAlpha * charged),
    );
    setTint(view.glow, color);

    view.call.visible = charge > 0 && reach !== undefined;
    if (view.call.visible && reach !== undefined) {
      const wide = CHARGE.from * reach;
      const closing = calm ? 0.5 : charge;
      view.call.position.set(zone.x, zone.y);
      view.call.scale.set((wide - (wide - CHARGE.to) * closing) / textures.ring.radius);
      view.call.alpha = shown * CHARGE.alpha * charged;
      setTint(view.call, white(frame));
    }

    view.ring.visible = true;
    view.ring.position.set(zone.x, zone.y);
    setTint(view.ring, color);
    if (calm) {
      view.ring.scale.set(zone.radius / textures.ring.radius);
      view.ring.alpha = shown * PULSE_RING.calmAlpha;
      return;
    }
    const phase = (now % beat) / beat;
    const out = Math.min(1, phase * PULSE_RING.speed);
    const eased = smoothstep(out);
    const radius = PULSE_RING.from + (zone.radius - PULSE_RING.from) * eased;
    view.ring.scale.set(radius / textures.ring.radius);
    view.ring.alpha = shown * PULSE_RING.alpha * (1 - phase) * (1 + PULSE_RING.front * front);
  }

  function called(event: SimEvent, state: SimState, frame: Frame): void {
    if (event.type !== 'skillUsed') {
      return;
    }
    const player = byId(state.players, event.playerId);
    if (player === undefined || !rappels.has(player.classId)) {
      return;
    }
    burster.spawn(frame, {
      shape: textures.ring,
      now: frame.now,
      duration: CALL.beats * frame.tempo.ticksPerBeat,
      x: player.x,
      y: player.y,
      fromRadius: CALL.from,
      toRadius: CALL.to,
      tint: white(frame),
      peak: CALL.alpha,
    });
  }

  return {
    reset(): void {
      landings.clear();
      sides.clear();
      lefts.clear();
      relit.clear();
      burster.bursts.clear();
    },
    onEvent: called,
    update(state: SimState, alpha: number, frame: Frame): void {
      if (state.props === undefined && state.zones === undefined && flying === undefined) {
        burster.bursts.update(state, alpha, frame);
        return;
      }
      image += 1;
      const { shapes } = sheet.get();
      drawFlying(state, alpha, frame, shapes);
      calling.clear();
      for (const prop of state.props ?? []) {
        if (prop.kind === RAPPEL) {
          calling.add(prop.playerId);
        }
      }
      planted ??= pool(shapes);
      planted.begin();
      for (const zone of state.zones ?? []) {
        const owner = zoneIds.has(zone.definition.id)
          ? byId(state.players, zone.playerId)
          : undefined;
        const attack = owner === undefined ? undefined : attackOf(owner);
        if (owner === undefined || attack === undefined) {
          continue;
        }
        const before = lefts.get(zone.id);
        if (before !== undefined && zone.ticksLeft > before) {
          relit.set(zone.id, frame.now);
          burst(zone, owner, frame);
        }
        lefts.set(zone.id, zone.ticksLeft);
        drawPlanted(planted.acquire(zone.id), zone, owner, attack, state, frame, alpha);
      }
      planted.end();
      for (const [id, landing] of landings) {
        if (image - landing.seen > 2) {
          landings.delete(id);
        }
      }
      for (const id of sides.keys()) {
        if (planted.peek(id) === undefined) {
          sides.delete(id);
          lefts.delete(id);
        }
      }
      for (const [id, at] of relit) {
        if (frame.now - at > RELIGHT.to * frame.tempo.ticksPerBeat) {
          relit.delete(id);
        }
      }
      burster.bursts.update(state, alpha, frame);
    },
    destroy(): void {
      burster.bursts.clear();
      sheet.painted?.source.destroy();
    },
  };
}
